import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack, Person, CalendarToday, Article } from "@mui/icons-material";
import eventos from "../data/eventos.js";

export const EventosDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const evento = eventos.find((e) => e.id === parseInt(id));

  if (!evento) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          pt: { xs: "80px", md: "100px" },
          pb: { xs: 4, md: 6 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="error" gutterBottom>
              Ministração não encontrada
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/eventos")}
              sx={{
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Voltar aos Eventos
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    // Se a string contém "a" (indicando um período), retorna como string
    if (
      typeof dateString === "string" &&
      (dateString.includes(" a ") || dateString.includes("-"))
    ) {
      return dateString;
    }

    // Caso contrário, tenta formatar como data
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Se não for uma data válida, retorna como string
      }
      return date.toLocaleDateString("pt-BR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateString; // Em caso de erro, retorna a string original
    }
  };

  const formatContent = (content) => {
    return content.split("\n").map(
      (paragraph, index) =>
        paragraph.trim() && (
          <Typography
            key={index}
            variant="body1"
            paragraph
            sx={{
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.1rem" },
              color: "#333",
              textAlign: "justify",
            }}
          >
            {paragraph.trim()}
          </Typography>
        ),
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        pt: { xs: "80px", md: "100px" },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="md">
        {/* Botão Voltar */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/eventos")}
          sx={{
            color: theme.palette.primary.main,
            fontWeight: "bold",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              backgroundColor: "rgba(139, 38, 53, 0.04)",
            },
          }}
        >
          Voltar aos Eventos
        </Button>

        {/* Conteúdo Principal */}
        <Paper
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          {/* Imagem de Destaque */}
          <Box
            component="img"
            src={evento.imagem}
            alt={evento.titulo}
            sx={{
              width: "100%",
              height: { xs: 250, md: 350 },
              objectFit: "cover",
              backgroundColor: "#f0f0f0",
            }}
            onError={(e) => {
              e.target.src = `data:image/svg+xml;base64,${btoa(`
                <svg width="800" height="400" xmlns="http://www.w3.org/2000/svg">
                  <rect width="100%" height="100%" fill="${theme.palette.primary.main}"/>
                  <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="24" 
                        fill="white" text-anchor="middle" dy="0.3em">
                    Imagem não encontrada
                  </text>
                </svg>
              `)}`;
            }}
          />

          {/* Conteúdo */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Meta informações */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 2, md: 3 },
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarToday sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "500" }}
                >
                  {formatDate(evento.data)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Person sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "500" }}
                >
                  {evento.autor}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Article sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "500" }}
                >
                  Evento
                </Typography>
              </Box>
            </Box>

            {/* Título */}
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: "bold",
                mb: 3,
                fontSize: { xs: "1.75rem", md: "2.125rem" },
                lineHeight: 1.3,
              }}
            >
              {evento.titulo}
            </Typography>

            {/* Resumo destacado */}
            <Paper
              sx={{
                backgroundColor: "rgba(139, 38, 53, 0.05)",
                border: "1px solid rgba(139, 38, 53, 0.1)",
                p: 3,
                mb: 4,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontStyle: "italic",
                  color: theme.palette.primary.dark,
                  fontWeight: "500",
                  lineHeight: 1.6,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                {`Link para inscrição: `}
                <a href={evento.link} target="_blank" rel="noopener noreferrer">
                  {evento.link}
                </a>
              </Typography>
            </Paper>

            {/* Conteúdo completo */}
            <Box sx={{ mt: 4 }}>{formatContent(evento.conteudo)}</Box>

            {/* Botão de ação final */}
            <Box sx={{ textAlign: "center", mt: 5 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate("/eventos")}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  fontWeight: "bold",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                Ver Mais Eventos
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
